/**
 * Custom Spin-the-Wheel component
 *
 * The winning amount is determined server-side via the submitSpinResult mutation.
 * The frontend calls the mutation first, receives the amount, then animates the
 * wheel to land on the correct segment.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useMutation } from '@apollo/client';

import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { colorPalette } from 'core/components/theme';
import { SUBMIT_SPIN_RESULT } from 'core/store/offer-store/offer-queries';
import useStyles from './custom-spin-wheel.styles';

// { bg, text } — alternating Beige75/Beige90 with Green75 for the biggest amount (index 4)
const SEGMENT_STYLES = [
  { bg: colorPalette.beige_75, text: colorPalette.white },
  { bg: colorPalette.beige_90, text: colorPalette.white },
  { bg: colorPalette.beige_75, text: colorPalette.white },
  { bg: colorPalette.beige_90, text: colorPalette.white },
  { bg: colorPalette.green_75, text: colorPalette.white }, // biggest amount
  { bg: colorPalette.beige_90, text: colorPalette.white },
  { bg: colorPalette.beige_75, text: colorPalette.white },
  { bg: colorPalette.beige_90, text: colorPalette.white }
];

const BUNDLE_MESSAGES = {
  10: "You've won $10 — nice.",
  80: "You've won $80 — nearly the top tier.",
  30: "You've won $30 — great work.",
  70: "You've won $70 — now we're moving.",
  200: "You've won $200 — a top-tier win.",
  40: "You've won $40 — well done.",
  50: "You've won $50 — that's solid.",
  100: "You've won $100 — that's a big one."
};

const MONOLINE_MESSAGES = {
  5: "You've won $5 — nice.",
  50: "You've won $50 — nearly the top tier.",
  25: "You've won $25 — great work.",
  40: "You've won $40 — now we're moving.",
  100: "You've won $100 — a top-tier win.",
  15: "You've won $15 — well done.",
  35: "You've won $35 — that's solid.",
  20: "You've won $20 — good job."
};

// Segment labels for the wheel visual (order matches the wheel layout)
const BUNDLE_SEGMENTS = [
  { amount: 10 },
  { amount: 80 },
  { amount: 30 },
  { amount: 70 },
  { amount: 200 },
  { amount: 40 },
  { amount: 50 },
  { amount: 100 }
];

const MONOLINE_SEGMENTS = [
  { amount: 5 },
  { amount: 50 },
  { amount: 25 },
  { amount: 40 },
  { amount: 100 },
  { amount: 15 },
  { amount: 35 },
  { amount: 20 }
];

let sharedAudioContext = null;

function getAudioContext() {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedAudioContext;
}

function playTick() {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = 800;
    gain.gain.value = 0.15;
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.05);
  } catch {
    // Audio not supported — silent fallback
  }
}

const FAST_SPIN_SPEED = 1000; // degrees per second during free spin — matches initial landing speed
const LANDING_DURATION = 2000; // ms for deceleration to target

// Easing function: decelerate smoothly to stop
function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

const CustomSpinWheel = ({ isBundle, offerId, onSpinComplete }) => {
  const classes = useStyles();
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const wheelRef = useRef(null);
  const lastSegmentRef = useRef(-1);
  const mountedRef = useRef(true);

  // Animation state refs (not React state — updated every frame)
  const animFrameRef = useRef(null);
  const currentAngleRef = useRef(0);
  const targetAngleRef = useRef(null); // null = free spinning, number = landing target
  const landingStartAngleRef = useRef(0);
  const landingStartTimeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    },
    []
  );

  const [submitSpin] = useMutation(SUBMIT_SPIN_RESULT);

  const segments = isBundle ? BUNDLE_SEGMENTS : MONOLINE_SEGMENTS;
  const segmentAngle = 360 / segments.length;

  // Build conic-gradient for the wheel
  const wheelBackground = useMemo(() => {
    const stops = segments.map((_seg, i) => {
      const { bg } = SEGMENT_STYLES[i % SEGMENT_STYLES.length];
      const start = (i * segmentAngle).toFixed(2);
      const end = ((i + 1) * segmentAngle).toFixed(2);
      return `${bg} ${start}deg ${end}deg`;
    });
    return `conic-gradient(from 0deg, ${stops.join(', ')})`;
  }, [segments, segmentAngle]);

  // Core animation loop — runs every frame while spinning
  const animate = useCallback(
    (timestamp) => {
      if (!wheelRef.current || !mountedRef.current) {
        return;
      }

      if (targetAngleRef.current !== null) {
        // LANDING PHASE: decelerate from current speed to stop at target
        const elapsed = timestamp - landingStartTimeRef.current;
        const progress = Math.min(elapsed / LANDING_DURATION, 1);
        const eased = easeOutCubic(progress);

        const totalLandingDistance = targetAngleRef.current - landingStartAngleRef.current;
        currentAngleRef.current = landingStartAngleRef.current + totalLandingDistance * eased;

        wheelRef.current.style.transform = `rotate(${currentAngleRef.current}deg)`;

        // Tick sound on segment crossing
        const normalizedAngle = ((currentAngleRef.current % 360) + 360) % 360;
        const currentSegment = Math.floor(normalizedAngle / segmentAngle);
        if (currentSegment !== lastSegmentRef.current) {
          lastSegmentRef.current = currentSegment;
          playTick();
        }

        if (progress < 1) {
          animFrameRef.current = requestAnimationFrame(animate);
        } else {
          // Landing complete
          animFrameRef.current = null;
        }
      } else {
        // FREE SPIN PHASE: constant speed rotation
        const delta = timestamp - lastFrameTimeRef.current;
        lastFrameTimeRef.current = timestamp;
        currentAngleRef.current += (FAST_SPIN_SPEED * delta) / 1000;

        wheelRef.current.style.transform = `rotate(${currentAngleRef.current}deg)`;

        // Tick sound on segment crossing
        const normalizedAngle = ((currentAngleRef.current % 360) + 360) % 360;
        const currentSegment = Math.floor(normalizedAngle / segmentAngle);
        if (currentSegment !== lastSegmentRef.current) {
          lastSegmentRef.current = currentSegment;
          playTick();
        }

        animFrameRef.current = requestAnimationFrame(animate);
      }
    },
    [segmentAngle]
  );

  // Start the free spin animation
  const startFreeSpin = useCallback(() => {
    targetAngleRef.current = null;
    lastFrameTimeRef.current = performance.now();
    animFrameRef.current = requestAnimationFrame(animate);
  }, [animate]);

  // Transition from free spin to landing on a target segment
  const startLanding = useCallback(
    (winnerIndex, onLanded) => {
      const segmentCenter = winnerIndex * segmentAngle + segmentAngle / 2;

      // Calculate landing distance so initial landing speed matches free spin speed
      // For easeOutCubic, initial velocity = 3 * distance / duration
      // So distance = speed * duration / 3
      const currentAngle = currentAngleRef.current;
      const idealDistance = (FAST_SPIN_SPEED * LANDING_DURATION) / 3000; // convert ms to seconds
      const remainder = currentAngle % 360;
      const targetOffset = 360 - segmentCenter;
      // Find the target angle that's closest to idealDistance away and lands on the right segment
      let target = currentAngle - remainder + targetOffset;
      while (target - currentAngle < idealDistance) {
        target += 360;
      }

      landingStartAngleRef.current = currentAngle;
      landingStartTimeRef.current = performance.now();
      targetAngleRef.current = target;

      // The animate loop will pick up the landing phase on next frame
      // Schedule the completion callback
      setTimeout(() => {
        if (mountedRef.current && onLanded) {
          onLanded();
        }
      }, LANDING_DURATION + 100);
    },
    [segmentAngle]
  );

  const handleSpin = useCallback(async () => {
    if (spinning || result) {
      return;
    }

    setSpinning(true);
    setError(null);

    // Start spinning immediately — no waiting for server
    startFreeSpin();

    try {
      const { data } = await submitSpin({ variables: { offerId, isBundle } });

      if (!mountedRef.current) {
        return;
      }

      const serverResult = data.submitSpinResult;

      if (serverResult.status === 'ALREADY_PROCESSED') {
        if (animFrameRef.current) {
          cancelAnimationFrame(animFrameRef.current);
        }
        setSpinning(false);
        if (onSpinComplete) {
          onSpinComplete(serverResult.amount);
        }
        return;
      }

      const winningAmount = serverResult.amount;
      const winnerIndex = segments.findIndex((seg) => seg.amount === winningAmount);
      if (winnerIndex === -1) {
        throw new Error(`Server returned unexpected amount: ${winningAmount}`);
      }

      // Seamlessly transition from free spin to landing
      startLanding(winnerIndex, () => {
        if (!mountedRef.current) {
          return;
        }
        setSpinning(false);
        setResult({ amount: winningAmount, notified: false });
        setTimeout(() => {
          if (!mountedRef.current) {
            return;
          }
          if (onSpinComplete) {
            onSpinComplete(winningAmount);
          }
          setResult({ amount: winningAmount, notified: true });
        }, 5000);
      });
    } catch (err) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      // Reset wheel back to starting position
      currentAngleRef.current = 0;
      targetAngleRef.current = null;
      if (wheelRef.current) {
        wheelRef.current.style.transform = 'rotate(0deg)';
      }
      setSpinning(false);
      setError('Something went wrong. Please try again.');
      console.error('[spin-the-wheel] Mutation failed:', err);
    }
  }, [spinning, result, segments, offerId, isBundle, submitSpin, onSpinComplete, startFreeSpin, startLanding]);

  // Don't render after parent has been notified
  if (result && result.notified) {
    return null;
  }

  return (
    <div className={classes.wrapper}>
      <Grid container direction="column" alignItems="center">
        <Label type="subtitle1" className={classes.titleText}>
          Nice work!
        </Label>
        <Label type="greenSmall" className={classes.subtitleText}>
          {isBundle
            ? 'Selling home & auto earned you a spin worth up to $200.'
            : 'Selling home or auto earned you a spin worth up to $100.'}
        </Label>

        <div className={classes.wheelContainer}>
          <div className={classes.pointer} />
          <div ref={wheelRef} className={classes.wheel} style={{ background: wheelBackground }}>
            {segments.map((seg, i) => {
              const angle = i * segmentAngle + segmentAngle / 2 - 90;
              const { text } = SEGMENT_STYLES[i % SEGMENT_STYLES.length];
              return (
                <div
                  key={seg.amount}
                  className={classes.segmentLabel}
                  style={{
                    transform: `rotate(${angle}deg) translateY(-50%)`,
                    color: text
                  }}
                >
                  ${seg.amount}
                </div>
              );
            })}
          </div>
          <div className={classes.centerCircle}>
            <svg className={classes.centerLogo} viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M22 0C34.131 0 44 9.869 44 22C44 34.131 34.131 44 22 44C16.298 44 11.097 41.82 7.184 38.25L6.692 37.787C2.567 33.786 0 28.187 0 22C0 9.869 9.869 0 22 0ZM22 2.117C11.036 2.117 2.117 11.036 2.117 22C2.117 32.964 11.036 41.883 22 41.883C32.964 41.883 41.883 32.964 41.883 22C41.883 11.036 32.964 2.117 22 2.117ZM22.815 8.703C23.055 8.703 23.255 8.876 23.297 9.105L23.305 9.193V21.649C23.305 21.914 23.597 22.059 23.805 21.925L23.859 21.882L30.027 15.812C30.194 15.647 30.451 15.627 30.64 15.752L30.717 15.815L31.653 16.751C31.824 16.922 31.842 17.187 31.708 17.378L31.651 17.446L23.619 25.374C23.447 25.544 23.339 25.768 23.311 26.006L23.305 26.126V27.856C23.305 28.07 23.537 28.188 23.707 28.088L23.761 28.047L30.027 21.88C30.194 21.715 30.451 21.695 30.64 21.82L30.717 21.883L31.653 22.819C31.824 22.99 31.842 23.255 31.708 23.446L31.651 23.514L23.706 31.357C23.486 31.574 23.348 31.859 23.313 32.163L23.305 32.316V35.488C23.305 35.728 23.131 35.928 22.903 35.97L22.815 35.978H21.491C21.251 35.978 21.051 35.805 21.009 35.576L21.001 35.488V29.161C21.001 28.816 20.881 28.484 20.665 28.219L20.566 28.11L10.707 18.251C10.537 18.081 10.519 17.817 10.651 17.626L10.707 17.558L11.643 16.623C11.813 16.453 12.077 16.434 12.268 16.566L12.336 16.623L20.49 24.777C20.661 24.949 20.944 24.853 20.994 24.634L21.001 24.565V19.643C21.001 19.288 20.878 18.947 20.656 18.675L20.554 18.563L15.4 13.409C15.23 13.239 15.211 12.975 15.344 12.784L15.4 12.717L16.336 11.781C16.506 11.611 16.77 11.592 16.961 11.724L17.029 11.781L20.553 15.305C20.702 15.454 20.946 15.374 20.993 15.186L21.001 15.119V9.193C21.001 8.952 21.175 8.752 21.403 8.711L21.491 8.703H22.815Z"
                fill={colorPalette.green_75}
              />
            </svg>
          </div>
        </div>

        {result ? (
          <div className={classes.resultPill}>
            <Label type="greenSmall">{(isBundle ? BUNDLE_MESSAGES : MONOLINE_MESSAGES)[result.amount]}</Label>
          </div>
        ) : (
          <>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSpin}
              disabled={spinning}
              style={{ marginTop: 24, width: 200 }}
            >
              {spinning ? 'Spinning...' : 'Spin now'}
            </Button>
            {error && (
              <Label type="greenSmall" className={classes.errorText}>
                {error}
              </Label>
            )}
          </>
        )}
      </Grid>
    </div>
  );
};

CustomSpinWheel.propTypes = {
  isBundle: PropTypes.bool.isRequired,
  offerId: PropTypes.string.isRequired,
  onSpinComplete: PropTypes.func
};

export default CustomSpinWheel;
